import {
  CONVOY_FRAME_DT,
  CONVOY_HEIGHTS,
  CONVOY_WORLD_H,
  CONVOY_WORLD_W,
  OVERWATCH,
  type ConvoyFrame,
  type ConvoyResult,
} from "@air-defence-sim/sim-core";
import type { ThemeName } from "../theme.js";
import { getConvoyPalette, type ConvoyPalette } from "./theme.js";

export type ConvoyRenderer = {
  drawFrame: (simT: number) => void;
  setResult: (r: ConvoyResult | null) => void;
  setTheme: (theme: ThemeName) => void;
  setDeadGround: (on: boolean) => void;
  resize: () => void;
  configure: (logicalWidth: number, logicalHeight: number, scale: number) => void;
  getLogicalSize: () => { width: number; height: number };
};

type Pt = { x: number; y: number };

export function makeConvoyRenderer(canvas: HTMLCanvasElement): ConvoyRenderer {
  const ctx = getCtx(canvas);
  let result: ConvoyResult | null = null;
  let palette: ConvoyPalette = getConvoyPalette("light");
  let logicalWidth = canvas.width;
  let logicalHeight = canvas.height;
  let deadGroundOn = true;
  let maskA: HTMLCanvasElement | null = null;
  let maskB: HTMLCanvasElement | null = null;

  const sx = (x: number): number => (x / CONVOY_WORLD_W) * logicalWidth;
  const sy = (y: number): number => (y / CONVOY_WORLD_H) * logicalHeight;
  const sr = (r: number): number => (r / CONVOY_WORLD_W) * logicalWidth;

  function configure(width: number, height: number, scale: number): void {
    logicalWidth = width;
    logicalHeight = height;
    canvas.width = Math.max(1, Math.floor(width * scale));
    canvas.height = Math.max(1, Math.floor(height * scale));
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
  }

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    configure(rect.width, rect.height, dpr);
  }

  /* ---------- dead-ground overlay ----------
     Shades the area where a drone at cruise altitude is invisible — the
     terrain shadow each forest block casts for a given sensor height. The
     convoy layer is the INTERSECTION of every alive vehicle's shadow (dead
     ground is only dead if nobody in the column can see it); the overwatch
     layer is the platform's own (near-empty) shadow. */

  function ensureMasks(): [HTMLCanvasElement, HTMLCanvasElement] {
    if (!maskA) maskA = document.createElement("canvas");
    if (!maskB) maskB = document.createElement("canvas");
    for (const m of [maskA, maskB]) {
      if (m.width !== canvas.width || m.height !== canvas.height) {
        m.width = canvas.width;
        m.height = canvas.height;
      }
    }
    return [maskA, maskB];
  }

  function signedArea(poly: Pt[]): number {
    let s = 0;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      if (a && b) s += a.x * b.y - b.x * a.y;
    }
    return s;
  }

  /**
   * Paint one sensor's terrain shadow (solid colour) into a mask canvas.
   * Geometry per block, for sensor height hs, canopy hc, target height ht:
   *  - hs ≤ hc: the canopy is a wall. Shadow extends from the near (front-
   *    facing) silhouette outward — to sensor-range cap if ht ≤ hc, else from
   *    the hidden-start multiplier k = (ht−hs)/(hc−hs).
   *  - hs > hc and ht < hc: the grazing ray over the far edge descends; the
   *    shadow is a finite skirt behind the far (back-facing) silhouette with
   *    multiplier k = (hs−ht)/(hs−hc).
   *  - Block interiors are always shaded: anything under the canopy is
   *    invisible to every sensor.
   */
  function paintSensorShadow(mask: HTMLCanvasElement, S: Pt, hs: number, rangeM: number, color: string): void {
    const g = mask.getContext("2d");
    if (!g || !result) return;
    const scale = canvas.width / logicalWidth;
    g.setTransform(scale, 0, 0, scale, 0, 0);
    g.clearRect(0, 0, logicalWidth, logicalHeight);
    g.fillStyle = color;
    const hc = result.config.canopy;
    const ht = CONVOY_HEIGHTS.droneCruise;
    const capM = rangeM * 1.25;
    const proj = (v: Pt, k: number): Pt => ({ x: S.x + (v.x - S.x) * k, y: S.y + (v.y - S.y) * k });

    for (const poly of result.world.blocks) {
      // Interior: under-canopy is invisible to everyone.
      g.beginPath();
      poly.forEach((p, i) => (i ? g.lineTo(sx(p.x), sy(p.y)) : g.moveTo(sx(p.x), sy(p.y))));
      g.closePath();
      g.fill();

      let wantBackFacing: boolean;
      let k1: number;
      let k2: number;
      if (hs <= hc) {
        wantBackFacing = false; // shadow starts at the near silhouette
        k1 = ht <= hc ? 1 : (ht - hs) / (hc - hs);
        k2 = Number.POSITIVE_INFINITY;
      } else if (ht < hc) {
        wantBackFacing = true; // finite skirt behind the far silhouette
        k1 = 1;
        k2 = (hs - ht) / (hs - hc);
      } else {
        continue; // sensor and target both above canopy: no shadow
      }

      const orient = Math.sign(signedArea(poly)) || 1;
      for (let i = 0; i < poly.length; i++) {
        const A = poly[i];
        const B = poly[(i + 1) % poly.length];
        if (!A || !B) continue;
        const cross = (B.x - A.x) * (S.y - A.y) - (B.y - A.y) * (S.x - A.x);
        const backFacing = cross * orient > 0;
        if (backFacing !== wantBackFacing) continue;
        const dA = Math.hypot(A.x - S.x, A.y - S.y) || 1;
        const dB = Math.hypot(B.x - S.x, B.y - S.y) || 1;
        const kA2 = Math.max(k1, Math.min(k2, capM / dA));
        const kB2 = Math.max(k1, Math.min(k2, capM / dB));
        const a1 = proj(A, k1);
        const b1 = proj(B, k1);
        const b2 = proj(B, kB2);
        const a2 = proj(A, kA2);
        g.beginPath();
        g.moveTo(sx(a1.x), sy(a1.y));
        g.lineTo(sx(b1.x), sy(b1.y));
        g.lineTo(sx(b2.x), sy(b2.y));
        g.lineTo(sx(a2.x), sy(a2.y));
        g.closePath();
        g.fill();
      }
    }
  }

  function compositeMask(mask: HTMLCanvasElement, alpha: number): void {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = alpha;
    ctx.drawImage(mask, 0, 0);
    ctx.restore();
  }

  function drawDeadGround(vehPts: Pt[], owPt: Pt | null): void {
    if (!result) return;
    const [a, b] = ensureMasks();
    const cfg = result.config;
    if (vehPts.length > 0) {
      const first = vehPts[0];
      if (!first) return;
      const hs = cfg.topCover === "mast" ? CONVOY_HEIGHTS.mastSensor : CONVOY_HEIGHTS.hullSensor;
      paintSensorShadow(a, first, hs, cfg.sensor, palette.deadGround);
      const actx = a.getContext("2d");
      if (!actx) return;
      for (let i = 1; i < vehPts.length; i++) {
        const p = vehPts[i];
        if (!p) continue;
        paintSensorShadow(b, p, hs, cfg.sensor, palette.deadGround);
        actx.setTransform(1, 0, 0, 1, 0, 0);
        actx.globalCompositeOperation = "destination-in";
        actx.drawImage(b, 0, 0);
        actx.globalCompositeOperation = "source-over";
      }
      compositeMask(a, palette.deadGroundAlpha);
    }
    if (owPt) {
      paintSensorShadow(a, owPt, CONVOY_HEIGHTS.overwatchAlt, OVERWATCH.sensorRange, palette.owHidden);
      compositeMask(a, palette.owHiddenAlpha);
    }
  }

  function roundedPoly(poly: Array<{ x: number; y: number }>): void {
    const n = poly.length;
    if (n < 3) return;
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % n];
      if (!a || !b) continue;
      const mx = sx((a.x + b.x) / 2);
      const my = sy((a.y + b.y) / 2);
      if (i === 0) ctx.moveTo(mx, my);
      else ctx.quadraticCurveTo(sx(a.x), sy(a.y), mx, my);
    }
    const a = poly[0];
    const b = poly[1];
    if (a && b) ctx.quadraticCurveTo(sx(a.x), sy(a.y), sx((a.x + b.x) / 2), sy((a.y + b.y) / 2));
    ctx.closePath();
  }

  function drawTerrain(): void {
    ctx.fillStyle = palette.ground;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);
    if (!result) return;
    const world = result.world;
    for (const m of world.meadows) {
      ctx.fillStyle = palette.meadow;
      ctx.beginPath();
      ctx.ellipse(sx(m.x), sy(m.y), sr(m.r), sr(m.r) * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // Road: casing, surface, centreline dashes.
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    world.road.forEach((pt, i) => (i ? ctx.lineTo(sx(pt.x), sy(pt.y)) : ctx.moveTo(sx(pt.x), sy(pt.y))));
    ctx.strokeStyle = palette.roadCasing;
    ctx.lineWidth = 7.5;
    ctx.stroke();
    ctx.strokeStyle = palette.roadSurface;
    ctx.lineWidth = 5.5;
    ctx.stroke();
    ctx.setLineDash([7, 9]);
    ctx.strokeStyle = palette.roadDash;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
    // Forest blocks with canopy speckle.
    for (const poly of world.blocks) {
      roundedPoly(poly);
      ctx.fillStyle = palette.forest;
      ctx.fill();
      ctx.strokeStyle = palette.forestEdge;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.fillStyle = palette.forestSpeckle;
    for (const poly of world.blocks) {
      const c = poly.reduce(
        (s, q) => ({ x: s.x + q.x / poly.length, y: s.y + q.y / poly.length }),
        { x: 0, y: 0 },
      );
      for (let i = 0; i < 10; i++) {
        const q = poly[i % poly.length];
        if (!q) continue;
        ctx.beginPath();
        ctx.arc(sx(c.x + (q.x - c.x) * 0.5), sy(c.y + (q.y - c.y) * 0.5), 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  function frameAt(simT: number): { fi: number; f: ConvoyFrame; g: ConvoyFrame; mix: number } | null {
    if (!result || result.frames.length === 0) return null;
    const last = result.frames.length - 1;
    const fIdx = Math.min(last, Math.max(0, Math.floor(simT / CONVOY_FRAME_DT)));
    const gIdx = Math.min(last, fIdx + 1);
    const f = result.frames[fIdx];
    const g = result.frames[gIdx];
    if (!f || !g) return null;
    const span = g.t - f.t;
    const mix = span > 0 ? Math.min(1, Math.max(0, (simT - f.t) / span)) : 0;
    return { fi: fIdx, f, g, mix };
  }

  function drawFrame(simT: number): void {
    ctx.clearRect(0, 0, logicalWidth, logicalHeight);
    drawTerrain();
    if (!result) return;
    const fr = frameAt(simT);
    if (!fr) return;
    const { fi, f, g, mix } = fr;
    const cfg = result.config;
    const lerp = (a: number, b: number): number => a + (b - a) * mix;

    // Interpolated positions of live vehicles and the overwatch platform.
    const vehPts: Pt[] = [];
    for (let i = 0; i < f.veh.length; i++) {
      const v = f.veh[i];
      if (!v || !v.a) continue;
      const w = g.veh[i] ?? v;
      vehPts.push({ x: lerp(v.x, w.x), y: lerp(v.y, w.y) });
    }
    const owPt: Pt | null =
      f.ow && g.ow ? { x: lerp(f.ow.x, g.ow.x), y: lerp(f.ow.y, g.ow.y) } : null;

    if (deadGroundOn) drawDeadGround(vehPts, owPt);

    // Sensor + engagement rings around live vehicles.
    for (const p of vehPts) {
      const x = sx(p.x);
      const y = sy(p.y);
      ctx.beginPath();
      ctx.arc(x, y, sr(cfg.sensor), 0, Math.PI * 2);
      ctx.strokeStyle = palette.rangeSensor;
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, sr(cfg.range), 0, Math.PI * 2);
      ctx.fillStyle = palette.rangeWeapon;
      ctx.fill();
      ctx.strokeStyle = palette.rangeWeaponEdge;
      ctx.lineWidth = 0.8;
      ctx.stroke();
    }

    // Overwatch orbit marker + engagement ring.
    if (f.ow && g.ow) {
      const x = sx(lerp(f.ow.x, g.ow.x));
      const y = sy(lerp(f.ow.y, g.ow.y));
      if (cfg.topCover === "armed") {
        ctx.beginPath();
        ctx.arc(x, y, sr(OVERWATCH.range), 0, Math.PI * 2);
        ctx.setLineDash([5, 6]);
        ctx.strokeStyle = palette.overwatchRing;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.fillStyle = palette.overwatch;
      ctx.strokeStyle = palette.overwatch;
      ctx.beginPath();
      ctx.arc(x, y, 4.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 9, y);
      ctx.lineTo(x + 9, y);
      ctx.moveTo(x, y - 9);
      ctx.lineTo(x, y + 9);
      ctx.stroke();
      ctx.font = "bold 9px ui-monospace, monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText("OW", x + 12, y);
    }

    // Event effects near t.
    for (const e of result.events) {
      const age = simT - e.t;
      if (age < 0) continue;
      if ((e.type === "shot" || e.type === "owshot") && age < 0.35) {
        const rgb = e.type === "shot" ? palette.shot : palette.owShot;
        ctx.strokeStyle = `rgba(${rgb}, ${1 - age / 0.35})`;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(sx(e.x1), sy(e.y1));
        ctx.lineTo(sx(e.x2), sy(e.y2));
        ctx.stroke();
      } else if (e.type === "kill" && age < 1.0) {
        ctx.strokeStyle = palette.kill;
        ctx.globalAlpha = 1 - age;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx(e.x), sy(e.y), 4 + age * 14, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      } else if (e.type === "impact" && age < 2.0) {
        ctx.strokeStyle = palette.impact;
        ctx.globalAlpha = 1 - age / 2;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(sx(e.x), sy(e.y), 5 + age * 16, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      } else if (e.type === "launch" && age < 3.5) {
        // Fading callout over each treeline as its drones come up — first-time
        // viewers otherwise read the red markers as dug-in ground teams.
        const fade = age < 2.5 ? 1 : 1 - (age - 2.5);
        const x = sx(e.x);
        const y = sy(e.y) - 16 - age * 3;
        ctx.globalAlpha = fade;
        ctx.font = "bold 10px ui-monospace, monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.lineWidth = 3;
        ctx.strokeStyle = palette.ground;
        ctx.strokeText("DRONE LAUNCH", x, y);
        ctx.fillStyle = palette.drone;
        ctx.fillText("DRONE LAUNCH", x, y);
        ctx.beginPath();
        ctx.moveTo(x, y + 4);
        ctx.lineTo(x - 4, y + 10);
        ctx.lineTo(x + 4, y + 10);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // Drones with short trails.
    for (let di = 0; di < f.dr.length; di++) {
      const d = f.dr[di];
      if (!d || !d.a) continue;
      const e = g.dr[di] ?? d;
      ctx.strokeStyle = palette.droneTrail;
      ctx.lineWidth = 1;
      ctx.beginPath();
      let started = false;
      for (let k = Math.max(0, fi - 8); k <= fi; k++) {
        const q = result.frames[k]?.dr[di];
        if (!q || !q.a) continue;
        if (!started) {
          ctx.moveTo(sx(q.x), sy(q.y));
          started = true;
        } else ctx.lineTo(sx(q.x), sy(q.y));
      }
      ctx.stroke();
      const x = lerp(d.x, e.x);
      const y = lerp(d.y, e.y);
      const prev = result.frames[Math.max(0, fi - 1)]?.dr[di] ?? d;
      const ang = Math.atan2(y - prev.y, x - prev.x);
      ctx.save();
      ctx.translate(sx(x), sy(y));
      ctx.rotate(ang);
      // Masked run under the canopy: viewer sees ground truth, but dimmed.
      if (d.h < cfg.canopy) ctx.globalAlpha = 0.45;
      drawDroneSprite(ctx, d.tr ? palette.drone : palette.droneUntracked);
      ctx.restore();
    }

    // Vehicles.
    for (let vi = 0; vi < f.veh.length; vi++) {
      const v = f.veh[vi];
      if (!v) continue;
      const w = g.veh[vi] ?? v;
      const x = sx(lerp(v.x, w.x));
      const y = sy(lerp(v.y, w.y));
      const heading = Math.atan2(lerp(v.hy, w.hy), lerp(v.hx, w.hx));
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(heading);
      if (v.a) {
        ctx.fillStyle = palette.vehicle;
        ctx.beginPath();
        ctx.roundRect(-9, -5.5, 18, 11, 3);
        ctx.fill();
        ctx.rotate(-heading);
        ctx.fillStyle = palette.vehicleLabel;
        ctx.font = "bold 7px ui-monospace, monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`V${vi + 1}`, 0, 0.5);
      } else {
        ctx.fillStyle = palette.vehicleDead;
        ctx.beginPath();
        ctx.roundRect(-9, -5.5, 18, 11, 3);
        ctx.fill();
        ctx.strokeStyle = "rgba(0, 0, 0, 0.45)";
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(-5, -4);
        ctx.lineTo(5, 4);
        ctx.moveTo(5, -4);
        ctx.lineTo(-5, 4);
        ctx.stroke();
        ctx.rotate(-heading);
        ctx.fillStyle = palette.vehicleSmoke;
        ctx.beginPath();
        ctx.arc(3, -9, 4, 0, Math.PI * 2);
        ctx.arc(7, -14, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // Scale bar (500 m).
    const bar = sr(500);
    ctx.strokeStyle = palette.scaleBar;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(logicalWidth - 24 - bar, logicalHeight - 18);
    ctx.lineTo(logicalWidth - 24, logicalHeight - 18);
    ctx.stroke();
    ctx.fillStyle = palette.scaleBar;
    ctx.font = "10px ui-monospace, monospace";
    ctx.textAlign = "right";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("500 m", logicalWidth - 24, logicalHeight - 24);
  }

  return {
    drawFrame,
    setResult: (r: ConvoyResult | null) => {
      result = r;
    },
    setTheme: (t: ThemeName) => {
      palette = getConvoyPalette(t);
    },
    setDeadGround: (on: boolean) => {
      deadGroundOn = on;
    },
    resize,
    configure,
    getLogicalSize: () => ({ width: logicalWidth, height: logicalHeight }),
  };
}

function getCtx(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const c = canvas.getContext("2d");
  if (!c) throw new Error("2d context unavailable");
  return c;
}

/**
 * Quadcopter glyph, drawn at the current origin with +x as the direction of
 * flight. Shared by the map and the legend so they always match.
 */
export function drawDroneSprite(g: CanvasRenderingContext2D, color: string, scale = 1): void {
  const ARM = 4.6;
  const arms = [Math.PI / 4, (3 * Math.PI) / 4, (-3 * Math.PI) / 4, -Math.PI / 4];
  g.save();
  g.scale(scale, scale);
  g.strokeStyle = color;
  g.fillStyle = color;
  g.lineWidth = 1.1;
  g.beginPath();
  for (const a of arms) {
    g.moveTo(0, 0);
    g.lineTo(Math.cos(a) * ARM, Math.sin(a) * ARM);
  }
  g.stroke();
  for (const a of arms) {
    g.beginPath();
    g.arc(Math.cos(a) * ARM, Math.sin(a) * ARM, 2, 0, Math.PI * 2);
    g.stroke();
  }
  g.beginPath();
  g.ellipse(0, 0, 3, 2, 0, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.moveTo(5.6, 0);
  g.lineTo(2.6, 1.4);
  g.lineTo(2.6, -1.4);
  g.closePath();
  g.fill();
  g.restore();
}
